import { Fragment, useState } from 'react';

import styled from 'styled-components';

import { Badge } from '@components/badge';
import { createForm, useCreateFormStore } from '@components/form';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconHourglass } from '@components/icons/hourglass';
import { IconSend } from '@components/icons/send';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { LabelTextGroup } from '@components/label-text-group';
import { NoResultCard } from '@components/no-result-card';
import { RadioGroup } from '@components/radio-group';
import { SelectOption } from '@components/select';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  MappableActionVariable,
  VariableValue,
} from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useUpdateJourneyTravellerAction } from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { ACTION_TYPES } from 'ui/actions/action-config';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';

import {
  AskAIInfoBox,
  AskAIInfoBoxClosable,
  getOutputValue,
  getPromptTemplateOptions,
  getPromptValue,
} from './helpers';

import {
  ActionProgressBar,
  FormSidebarFooter,
  MappedSource,
  SidebarProps,
  TravellerActionFooter,
  TravellerActionSidebar,
  TriggerMethod,
  isActionError,
  useEditingContext,
} from '../common';
import { getFieldMappingId } from '../helpers';

const ACTION_TYPE = ACTION_TYPES.ask_ai;

export const SidebarContent = ({ shortId }: SidebarProps) => (
  <TravellerActionSidebar
    shortId={shortId}
    actionType={ACTION_TYPE}
    Details={DetailsContent}
    Edit={FormContent}
  />
);

type ContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
};

const FieldMappedTag = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
  space: 1,
})({
  backgroundColor: theme.color.blueTrans10,
  borderColor: theme.color.elementBlue100,
  borderWidth: pxToRem(1),
  borderStyle: 'solid',
  color: theme.color.elementBlue100,
  borderRadius: pxToRem(16),
  padding: `${pxToRem(0)} ${pxToRem(8)}`,
  height: pxToRem(24),
  display: 'inline-flex',
  maxWidth: pxToRem(400),
  overflow: 'hidden',
  flexShrink: 0,
});

const TextWithTagsContainer = styled(Text)({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: pxToRem(4),
  width: '100%',
});

/*
We probably want to refactor this to use a more generic approach to rendering text with tags.
I'll keep this as is for now, but once we use this in other places, we should move it to core
and possibly rewrite it.
*/
const TextWithTags = ({ data }: { data: VariableValue | undefined }) => {
  // Function to replace variables with tags
  const renderTextWithTags = (value: string, variables: VariableValue['variables']) => {
    const parts = value.split(/(\{\{[^}]+\}\})/g);
    return parts.map((part, index) => {
      const match = part.match(/\{\{([^}]+)\}\}/);
      if (match) {
        const variableKey = match[1].trim();
        const variable = variables?.[variableKey];
        if (variable) {
          return (
            <FieldMappedTag key={index}>
              <IconTextFormatLink />
              <Text
                font="semibold12"
                color="elementBlue100"
                text={variable.label}
                ellipsis
                style={{ minWidth: 0, flex: 1 }}
              />
            </FieldMappedTag>
          );
        }
      }
      return part;
    });
  };
  if (!data) {
    return null;
  }

  return (
    <TextWithTagsContainer>
      {renderTextWithTags(data.value, data.variables ?? {})}
    </TextWithTagsContainer>
  );
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const isError = isActionError(action);

  const referenceVariables = action.values?.field.variables ?? {};
  const firstReferenceVariable = Object.values(referenceVariables)[0] as MappableActionVariable;
  const fieldLabel = firstReferenceVariable?.label ?? '';
  const isAttributeReference = Boolean(
    firstReferenceVariable?.source.startsWith('company_traveller_attribute') ||
      firstReferenceVariable?.source.startsWith('journey_attribute')
  );
  const referenceActionId =
    !isAttributeReference && firstReferenceVariable ? firstReferenceVariable.action : '';

  const promptLabel = <TextWithTags data={action.values?.prompt} />;
  const outputType = action.values?.outputType as OutputType;
  const output = action.values?.output as VariableValue[];
  const isComplete = action.status === 'completed';

  return (
    <Stack space={6}>
      <ActionProgressBar action={action} isError={isError} isCard={false} />
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <Stack space={4}>
          <LabelTextGroup label={t('description')} text={action.description} />
          <div>
            <Text font="semibold14" text={t('field')} />
            <MappedSource
              label={fieldLabel ?? ''}
              isAttribute={isAttributeReference}
              isActionMapped={Boolean(referenceActionId)}
              referenceActionId={referenceActionId}
              isUploaded={false}
            />
          </div>
          <LabelTextGroup
            label={t('prompt')}
            text={isComplete ? action.data.prompt : promptLabel}
          />
          <LabelTextGroup label={t('output')} text={t(outputType ?? 'text')} />
          {outputType === 'tokens' && (
            <div>
              <Text font="semibold14" text={t('tokens')} />
              <Stack space={2} orientation="horizontal">
                {(output as VariableValue[]).map((token) => (
                  <Badge key={token.value} size="md" text={token.value} />
                ))}
              </Stack>
            </div>
          )}
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('output')}>
        <Output action={action} />
      </HideContainer>
      <Sidebar.Separator />
      <TravellerActionFooter
        action={action}
        startIcon={<IconSend />}
        startText={t('run')}
        retryText={t('retry')}
      />
    </Stack>
  );
};

const Output = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  if (action.status === 'completed') {
    if (action.values?.outputType === 'tokens') {
      return (
        <Stack space={4}>
          <div>
            <Badge variant="green" size="md" text={action.data.response} />
          </div>
          <LabelTextGroup label="Reason" text={action.data.responseReason} />
        </Stack>
      );
    } else {
      return <Text font="regular14" text={action.data.response} />;
    }
  }
  return <NoResultCard icon={IconHourglass} text={t('actionNotRun')} />;
};

type OutputType = 'tokens' | 'text';

type Data = {
  id: string;
  description: string;
  isAutomatic: boolean;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
  field: string;
  template: string;
  prompt: string;
  outputType: OutputType;
  output: string;
};

const Form = createForm<Data>();

const FormContent = ({ action }: ContentProps) => {
  const { stopEditing } = useEditingContext();
  const { t } = useScopedTranslate(sidebarLocale);
  const formStore = useCreateFormStore();
  const templateValue = formStore.useFieldValue('template') as string;
  const outputType = formStore.useFieldValue('outputType') as OutputType;
  const isDirty = formStore.useIsDirty();
  const [newData, setNewData] = useState<
    { prompt: VariableValue; outputType: OutputType; output: VariableValue[] } | undefined
  >(undefined);
  const fieldMappingId = getFieldMappingId(action.journeyId);

  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const handleSave = async (data: Data) => {
    await editMutation.mutateAsync({
      description: data.description,
      isAutomatic: true,
      action: 'ask_ai',
      values: {
        field: data.field,
        template: data.template,
        prompt: data.prompt,
        outputType: data.outputType,
        output: data.output ?? [],
      },
    });
  };

  const variableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    excludedTypes: [],
  });

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('input')}>
          <Stack space={4}>
            <AskAIInfoBoxClosable infoType="field" />
            <Form.FieldMappingField
              id="field"
              label={t('field')}
              variableGroups={variableGroups}
              defaultValue={action.values?.field}
              singleLine
              plaintext
              validators={['required']}
              info={t('fieldInfo')}
              fieldMappingId={fieldMappingId}
            />
            <AskAIInfoBox infoType="askAI" />
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('prompt')}>
          <Stack space={4}>
            <AskAIInfoBoxClosable infoType="prompt" />
            <Form.Select
              id="template"
              options={getPromptTemplateOptions()}
              label={t('template')}
              defaultValue={action.values?.template}
              // @ts-ignore
              onChange={(templateType: TemplateName) => {
                setNewData({
                  prompt: getPromptValue(templateType),
                  outputType: getOutputValue(templateType).length > 0 ? 'tokens' : 'text',
                  output: getOutputValue(templateType),
                });
              }}
              placeholder={t('selectTemplate')}
              validators={['required']}
            />
            <Fragment key={templateValue}>
              <Form.FieldMappingTextEditor
                id="prompt"
                label={t('prompt')}
                variableGroups={variableGroups}
                defaultValue={newData?.prompt ?? action.values?.prompt}
                plaintext
                validators={['required']}
                fieldMappingId={fieldMappingId}
              />
            </Fragment>
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('output')} key={templateValue}>
          <Stack space={4}>
            <Form.RadioGroup
              label={t('output')}
              id="outputType"
              hideLabel
              defaultValue={newData?.outputType ?? action.values?.outputType}
            >
              <RadioGroup.Option label={t('tokens')} key="token" value="tokens" />
              <RadioGroup.Option label={t('text')} key="text" value="text" />
            </Form.RadioGroup>
            {outputType === 'tokens' ? (
              <>
                <Form.TokenListField
                  id="output"
                  label={t('output')}
                  hideLabel
                  defaultValue={newData?.output ?? action.values?.output}
                />
                <AskAIInfoBox infoType="outputToken" />
              </>
            ) : (
              <AskAIInfoBox infoType="outputText" />
            )}
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
      </Stack>
      <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    dataNotFound: 'Data not found',
    description: 'Description',
    run: 'Run',
    retry: 'Retry',
    template: 'Template',
    input: 'Input',
    tokens: 'Tokens',
    text: 'Text',
    actionNotRun: 'Action has not been run',
    selectTemplate: 'Select template',
    actionDetails: 'Action details',
    output: 'Output',
    prompt: 'Prompt',
    selectFunction: 'Select function',
    noFunctionsAvailable: 'No functions available for selected field',
    field: 'Field',
    fieldInfo: 'Field to format',
    value: 'Value',
  },
};
